/* eslint-disable react/no-unescaped-entities */
/**
 * KUHAS Wave 1 admin hub — 5 screens under tab navigation.
 *
 * Screens (matching the spec):
 *   1. Master Course Builder      → tab="courses"
 *   2. KAPE Review Queue          → tab="kape"
 *   3. Program Builder            → tab="programs"
 *   4. Offering Creator           → tab="offerings"
 *   5. Revision Request Form+List → tab="revisions"
 *
 * This is functional-only per spec — no styling polish. All screens
 * hit the /api/kuhas/* endpoints. Enforcement rules (approved =
 * uneditable, offering fields immutable, etc.) live on the backend;
 * the UI just surfaces the enforcement (disables inputs, shows banners)
 * so users know why they can't do something.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("kcis_admin_token") || localStorage.getItem("admin_token") || ""}` });

const TABS = [
  ["courses", "Master Courses"],
  ["kape", "KAPE Queue"],
  ["programs", "Programs"],
  ["offerings", "Offerings"],
  ["revisions", "Revisions"],
];

export default function AdminKuhas() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "courses";
  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="kuhas-admin">
      <header className="max-w-6xl mx-auto mb-6">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">KUHAS · Wave 1</div>
        <h1 className="font-serif text-3xl text-slate-900">Curriculum Architecture</h1>
        <p className="text-sm text-slate-600 mt-1">
          Master Courses built once, permanent. Offerings vary only by instructor, voice, schedule.
        </p>
      </header>
      <nav className="max-w-6xl mx-auto mb-4 flex gap-2 flex-wrap">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setSp({ tab: k })}
            data-testid={`kuhas-tab-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              tab === k ? "bg-purple-900 text-white" : "bg-white text-slate-700 border border-slate-200"
            }`}
          >
            {l}
          </button>
        ))}
      </nav>
      <main className="max-w-6xl mx-auto">
        {tab === "courses" && <MasterCoursesTab />}
        {tab === "kape" && <KapeQueueTab />}
        {tab === "programs" && <ProgramsTab />}
        {tab === "offerings" && <OfferingsTab />}
        {tab === "revisions" && <RevisionsTab />}
      </main>
    </div>
  );
}

/* ─────────── shared helpers ─────────── */
function Card({ title, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-5" data-testid={testId}>
      {title && <h2 className="font-serif text-lg text-slate-900 mb-3">{title}</h2>}
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-purple-900";
const btnPrimary = "rounded-full bg-purple-900 text-white px-4 py-2 text-xs disabled:opacity-40";
const btnGhost = "rounded-full bg-white border border-slate-300 text-slate-700 px-3 py-1.5 text-xs";
const badge = (s, tone = "slate") => (
  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-${tone}-100 text-${tone}-800`}>
    {s}
  </span>
);

/* ═══════════════ 1. Master Course Builder ═══════════════ */
function MasterCoursesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ course_code: "", title: "", objectives: "" });
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kuhas/master-courses`, { headers: auth() });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create() {
    try {
      const r = await axios.post(`${API}/kuhas/master-courses`, {
        course_code: form.course_code, title: form.title,
        objectives: form.objectives.split("\n").map((s) => s.trim()).filter(Boolean),
      }, { headers: auth() });
      toast.success("Master course created (draft)");
      setForm({ course_code: "", title: "", objectives: "" });
      setSelectedId(r.data._id);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  }

  const selected = rows.find((c) => c._id === selectedId);
  return (
    <>
      <Card title="Create new Master Course" testId="mc-create">
        <Field label="Course code (e.g. TRI-101)">
          <input className={inputCls} value={form.course_code}
            onChange={(e) => setForm({ ...form, course_code: e.target.value })}
            data-testid="mc-course-code" />
        </Field>
        <Field label="Title">
          <input className={inputCls} value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="mc-title" />
        </Field>
        <Field label="Objectives (one per line)">
          <textarea className={inputCls} rows={3} value={form.objectives}
            onChange={(e) => setForm({ ...form, objectives: e.target.value })}
            data-testid="mc-objectives" />
        </Field>
        <button onClick={create} disabled={!form.course_code || !form.title}
          className={btnPrimary} data-testid="mc-create-submit">
          Create draft
        </button>
      </Card>

      <Card title={`All Master Courses (${rows.length})`} testId="mc-list">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <ul className="space-y-1.5 text-sm">
            {rows.map((c) => (
              <li key={c._id} data-testid={`mc-row-${c._id}`}>
                <button onClick={() => setSelectedId(c._id)}
                  className={`w-full text-left rounded-lg px-3 py-2 ${selectedId === c._id ? "bg-purple-50 border border-purple-300" : "border border-slate-100"}`}>
                  <span className="font-mono text-xs mr-2 text-purple-900">{c.course_code}</span>
                  <span>{c.title}</span>
                  <span className="ml-2">{badge(`v${c.version} · ${c.status}`, c.status === "approved" ? "emerald" : c.status === "draft" ? "slate" : "amber")}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selected && <MasterCourseDetail course={selected} onChange={load} />}
    </>
  );
}

function MasterCourseDetail({ course, onChange }) {
  const [detail, setDetail] = useState(null);
  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/kuhas/master-courses/${course._id}`, { headers: auth() });
    setDetail(r.data);
  }, [course._id]);
  useEffect(() => { reload(); }, [reload]);
  const editable = course.status === "draft";

  return (
    <Card testId="mc-detail" title={`${course.course_code} — ${course.title}`}>
      <div className="text-xs text-slate-500 mb-3">Status: {badge(course.status)} · v{course.version}</div>
      {!editable && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 text-xs text-amber-900" data-testid="mc-locked-banner">
          This course is not in draft. Content edits are blocked at the backend. Use the
          <b> Revisions</b> tab to open a Revision Request — approval creates a new draft version.
        </div>
      )}
      <AssetPanel course={course} editable={editable} onAttach={() => { reload(); onChange(); }} />
      <SequenceList detail={detail} />
      {editable && (
        <div className="mt-4 flex gap-2">
          <button data-testid="mc-submit-review"
            className={btnPrimary}
            onClick={async () => {
              try {
                await axios.post(`${API}/kuhas/master-courses/${course._id}/submit-for-review`, {}, { headers: auth() });
                toast.success("Submitted for KAPE review");
                onChange();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }}>
            Submit for KAPE Review
          </button>
        </div>
      )}
    </Card>
  );
}

function SequenceList({ detail }) {
  if (!detail?.sequence?.length) return <div className="text-xs text-slate-500">No assets attached yet.</div>;
  return (
    <ol className="space-y-1.5 mt-3" data-testid="mc-sequence">
      {detail.sequence.map((s) => (
        <li key={s.link_id} className="text-sm border border-slate-100 rounded-lg px-3 py-2">
          <span className="font-mono text-xs text-slate-500 mr-2">#{s.sequence_position}</span>
          <span>{s.asset?.title}</span>
          <span className="ml-2 text-[11px] text-slate-500">
            {s.asset?.type} · {s.asset?.complexity_level} · {s.asset?.status}
          </span>
        </li>
      ))}
    </ol>
  );
}

function AssetPanel({ course, editable, onAttach }) {
  const [query, setQuery] = useState({ type: "lesson_block", complexity_level: "intro", domain_tags: "trichology" });
  const [searchResult, setSearchResult] = useState(null);
  const [genBrief, setGenBrief] = useState("");
  const [genTitle, setGenTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [seqPos, setSeqPos] = useState(1);

  async function doSearch() {
    setBusy(true);
    try {
      const tags = query.domain_tags.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await axios.post(`${API}/kuhas/course-factory/search-before-generate`, {
        type: query.type, complexity_level: query.complexity_level, domain_tags: tags,
      }, { headers: auth() });
      setSearchResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    setBusy(false);
  }
  async function generate() {
    if (!searchResult?.search_token) return toast.error("Run reuse-first search first");
    if (!genBrief || !genTitle) return toast.error("Title + brief required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kuhas/course-factory/generate-asset`, {
        search_token: searchResult.search_token,
        type: query.type, complexity_level: query.complexity_level,
        domain_tags: query.domain_tags.split(",").map((s) => s.trim()).filter(Boolean),
        brief: genBrief, title: genTitle,
      }, { headers: auth() });
      toast.success(`Generated draft asset: ${r.data.title}`);
      await attach(r.data._id);
      setGenBrief(""); setGenTitle("");
      setSearchResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Generate failed"); }
    setBusy(false);
  }
  async function attach(assetId) {
    try {
      await axios.post(`${API}/kuhas/master-courses/${course._id}/asset-links`, {
        asset_id: assetId, asset_version: 1, sequence_position: seqPos,
      }, { headers: auth() });
      toast.success("Attached to course");
      setSeqPos(seqPos + 1);
      onAttach();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Attach failed"); }
  }

  if (!editable) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-4 mt-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Reuse-first search + attach</div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
        <select className={inputCls} value={query.type} onChange={(e) => setQuery({ ...query, type: e.target.value })} data-testid="as-type">
          {["lesson_block", "case_study", "worksheet", "quiz_item", "image", "infographic", "scenario"].map(t => <option key={t}>{t}</option>)}
        </select>
        <select className={inputCls} value={query.complexity_level} onChange={(e) => setQuery({ ...query, complexity_level: e.target.value })} data-testid="as-complexity">
          {["intro", "intermediate", "advanced", "doctoral"].map(c => <option key={c}>{c}</option>)}
        </select>
        <input className={inputCls} placeholder="tags, comma-separated" value={query.domain_tags}
          onChange={(e) => setQuery({ ...query, domain_tags: e.target.value })} data-testid="as-tags" />
        <input className={inputCls} type="number" min={1} value={seqPos}
          onChange={(e) => setSeqPos(parseInt(e.target.value || "1", 10))} data-testid="as-seq" />
      </div>
      <div className="flex gap-2 mb-3">
        <button onClick={doSearch} disabled={busy} className={btnPrimary} data-testid="as-search">
          {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Search reuse-first"}
        </button>
      </div>
      {searchResult && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs" data-testid="as-search-result">
          <div className="text-slate-600 mb-2">
            {searchResult.count} existing match(es) — {searchResult.reuse_first_notice}
          </div>
          {searchResult.matches.slice(0, 5).map((m) => (
            <div key={m._id} className="flex items-center justify-between border-t border-slate-200 py-1.5">
              <div>
                <span className="font-medium">{m.title}</span>
                <span className="ml-2 text-slate-500">{m.status} · used {m.reuse_count}×</span>
              </div>
              <button onClick={() => attach(m._id)} className={btnGhost} data-testid={`as-attach-${m._id}`}>
                Attach at #{seqPos}
              </button>
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-slate-300">
            <div className="text-slate-600 mb-2">No good match? Generate with Course Factory (draft — will need KAPE):</div>
            <input className={inputCls + " mb-1.5"} placeholder="Asset title" value={genTitle}
              onChange={(e) => setGenTitle(e.target.value)} data-testid="as-gen-title" />
            <textarea className={inputCls + " mb-2"} rows={2} placeholder="Brief for Claude" value={genBrief}
              onChange={(e) => setGenBrief(e.target.value)} data-testid="as-gen-brief" />
            <button onClick={generate} disabled={busy} className={btnPrimary} data-testid="as-generate">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Generate + attach"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ 2. KAPE Review Queue ═══════════════ */
function KapeQueueTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    // Aggregate pending: assets(draft) + master-courses(kape_review) + programs(kape_review) + revisions(submitted)
    const [a, mc, p, rv] = await Promise.all([
      axios.get(`${API}/kuhas/assets?status=draft`, { headers: auth() }),
      axios.get(`${API}/kuhas/master-courses?status=kape_review`, { headers: auth() }),
      axios.get(`${API}/kuhas/programs?status=kape_review`, { headers: auth() }),
      axios.get(`${API}/kuhas/revision-requests?status=submitted`, { headers: auth() }),
    ]);
    const list = [];
    (a.data.items || []).forEach(x => list.push({ kind: "asset", _id: x._id, title: x.title, sub: `${x.type} · ${x.complexity_level}`, ver: x.version, created_at: x.created_at, raw: x }));
    (mc.data.items || []).forEach(x => list.push({ kind: "master_course", _id: x._id, title: x.title, sub: x.course_code, ver: x.version, created_at: x.created_at, raw: x }));
    (p.data.items || []).forEach(x => list.push({ kind: "program", _id: x._id, title: x.title, sub: x.program_code, ver: x.version, created_at: x.created_at, raw: x }));
    (rv.data.items || []).forEach(x => list.push({ kind: "revision", _id: x._id, title: `${x.target_type} revision`, sub: x.reason, ver: x.current_version, created_at: x.created_at, raw: x }));
    list.sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
    setItems(list);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function openItem(item) {
    setSelected(item);
    if (item.kind !== "revision") {
      const r = await axios.get(`${API}/kuhas/kape-reviews`, {
        params: { target_id: item._id, target_type: item.kind }, headers: auth(),
      });
      setHistory(r.data.items || []);
    } else setHistory([]);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card title={`Pending KAPE (${items.length})`} testId="kape-list">
        {items.length === 0 && <div className="text-sm text-slate-500">Queue is clear.</div>}
        <ul className="space-y-1.5 text-sm">
          {items.map(it => (
            <li key={`${it.kind}-${it._id}`}>
              <button onClick={() => openItem(it)}
                data-testid={`kape-item-${it.kind}-${it._id}`}
                className={`w-full text-left rounded-lg px-3 py-2 border ${selected?._id === it._id ? "bg-purple-50 border-purple-300" : "border-slate-100"}`}>
                <span className="mr-2">{badge(it.kind)}</span>
                <span className="font-medium">{it.title}</span>
                <div className="text-xs text-slate-500 mt-0.5">{it.sub} · v{it.ver}</div>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      {selected && <KapeDetail item={selected} history={history} onDone={() => { load(); setSelected(null); }} />}
    </div>
  );
}

function KapeDetail({ item, history, onDone }) {
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState('{"academic_rigor": true, "voice_consistency": true}');
  const [busy, setBusy] = useState(false);

  async function decide(decision) {
    setBusy(true);
    let checklistObj = {};
    try { checklistObj = JSON.parse(checklist); } catch { toast.error("Checklist must be JSON"); setBusy(false); return; }
    try {
      if (item.kind === "revision") {
        await axios.post(`${API}/kuhas/revision-requests/${item._id}/decision`,
          { decision: decision === "approved" ? "approved" : "rejected", decision_notes: notes },
          { headers: auth() });
      } else {
        const url = item.kind === "asset" ? `${API}/kuhas/assets/${item._id}/kape-review`
          : item.kind === "master_course" ? `${API}/kuhas/master-courses/${item._id}/kape-review`
          : `${API}/kuhas/programs/${item._id}/kape-review`;
        await axios.post(url, {
          target_type: item.kind, target_id: item._id, target_version: item.ver,
          decision, checklist_results: checklistObj, notes,
        }, { headers: auth() });
      }
      toast.success(`${decision}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  }

  return (
    <Card title={item.title} testId="kape-detail">
      <pre className="text-[11px] bg-slate-50 rounded p-3 max-h-64 overflow-auto">{JSON.stringify(item.raw, null, 2)}</pre>
      <Field label="Checklist results (JSON)">
        <textarea className={inputCls} rows={3} value={checklist} onChange={(e) => setChecklist(e.target.value)} data-testid="kape-checklist" />
      </Field>
      <Field label="Notes">
        <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="kape-notes" />
      </Field>
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => decide("approved")} className={btnPrimary} data-testid="kape-approve">Approve</button>
        {item.kind !== "revision" ? (
          <button disabled={busy} onClick={() => decide("revisions_required")} className={btnGhost} data-testid="kape-revise">Revisions Required</button>
        ) : (
          <button disabled={busy} onClick={() => decide("rejected")} className={btnGhost} data-testid="kape-reject">Reject</button>
        )}
      </div>
      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Review history</div>
          <ul className="text-xs space-y-1">
            {history.map((h) => (
              <li key={h._id} className="border-l-2 border-slate-200 pl-2">
                <b>{h.decision}</b> · v{h.target_version} · {h.reviewer_id} · {(h.reviewed_at || "").slice(0, 16)}
                {h.notes && <div className="italic text-slate-500">"{h.notes}"</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/* ═══════════════ 3. Program Builder ═══════════════ */
function ProgramsTab() {
  const [approvedCourses, setApprovedCourses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ program_code: "", title: "" });
  const [seq, setSeq] = useState([]);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      axios.get(`${API}/kuhas/master-courses?status=approved`, { headers: auth() }),
      axios.get(`${API}/kuhas/programs`, { headers: auth() }),
    ]);
    setApprovedCourses(c.data.items || []);
    setPrograms(p.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create() {
    if (!form.program_code || !form.title) return;
    try {
      const r = await axios.post(`${API}/kuhas/programs`, {
        program_code: form.program_code, title: form.title,
        course_sequence: seq.map((c, i) => ({
          master_course_id: c._id, required_version: c.version, sequence_position: i + 1,
        })),
      }, { headers: auth() });
      toast.success(`Program created (draft, v${r.data.version})`);
      setForm({ program_code: "", title: "" }); setSeq([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
  }
  return (
    <>
      <Card title="Create new Program" testId="prog-create">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Program code" value={form.program_code}
            onChange={(e) => setForm({ ...form, program_code: e.target.value })} data-testid="prog-code" />
          <input className={inputCls} placeholder="Title" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="prog-title" />
        </div>
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Sequence (approved courses only)</div>
          <ol className="space-y-1 mb-2">
            {seq.map((c, i) => (
              <li key={c._id} className="text-sm flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
                <span><b>#{i + 1}</b> · {c.course_code} — {c.title} (v{c.version})</span>
                <button className={btnGhost} onClick={() => setSeq(seq.filter((_, j) => j !== i))}>Remove</button>
              </li>
            ))}
          </ol>
          <select className={inputCls}
            onChange={(e) => {
              const c = approvedCourses.find((x) => x._id === e.target.value);
              if (c) setSeq([...seq, c]);
              e.target.value = "";
            }}
            data-testid="prog-add-course"
            defaultValue=""
          >
            <option value="">— add an approved course —</option>
            {approvedCourses.map((c) => (
              <option key={c._id} value={c._id}>{c.course_code} — {c.title} (v{c.version})</option>
            ))}
          </select>
        </div>
        <button onClick={create} disabled={!form.program_code || !form.title || seq.length === 0}
          className={btnPrimary + " mt-3"} data-testid="prog-submit">Create draft</button>
      </Card>
      <Card title={`Programs (${programs.length})`} testId="prog-list">
        <ul className="space-y-1.5 text-sm">
          {programs.map((p) => (
            <li key={p._id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2">
              <span>
                <span className="font-mono text-xs text-purple-900 mr-2">{p.program_code}</span>
                {p.title} · v{p.version} {badge(p.status, p.status === "approved" ? "emerald" : "slate")}
              </span>
              {p.status === "draft" && (
                <button className={btnGhost} data-testid={`prog-submit-review-${p._id}`}
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/kuhas/programs/${p._id}/submit-for-review`, {}, { headers: auth() });
                      toast.success("Submitted for KAPE"); load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}>Submit for KAPE</button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/* ═══════════════ 4. Offering Creator ═══════════════ */
function OfferingsTab() {
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [form, setForm] = useState({
    master_course_id: "", professor_id: "", cohort_name: "",
    start_date: "", end_date: "", schedule: "", office_hours: "",
  });
  const [voicePreview, setVoicePreview] = useState(null);

  const load = useCallback(async () => {
    const [c, i, o] = await Promise.all([
      axios.get(`${API}/kuhas/master-courses?status=approved`, { headers: auth() }),
      axios.get(`${API}/kuhas/instructors`, { headers: auth() }),
      axios.get(`${API}/kuhas/offerings`, { headers: auth() }),
    ]);
    setCourses(c.data.items || []);
    setInstructors(i.data.items || []);
    setOfferings(o.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function pickInstructor(id) {
    setForm({ ...form, professor_id: id });
    if (id) {
      const r = await axios.get(`${API}/kuhas/instructors/${id}`, { headers: auth() });
      setVoicePreview(r.data.active_voice_profile);
    } else setVoicePreview(null);
  }
  async function create() {
    try {
      await axios.post(`${API}/kuhas/offerings`, form, { headers: auth() });
      toast.success("Offering created — course version + voice locked");
      setForm({ master_course_id: "", professor_id: "", cohort_name: "", start_date: "", end_date: "", schedule: "", office_hours: "" });
      setVoicePreview(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
  }

  return (
    <>
      <Card title="Create Offering" testId="off-create">
        <select className={inputCls} value={form.master_course_id}
          onChange={(e) => setForm({ ...form, master_course_id: e.target.value })}
          data-testid="off-course">
          <option value="">— approved master course —</option>
          {courses.map((c) => <option key={c._id} value={c._id}>{c.course_code} — {c.title} (v{c.version})</option>)}
        </select>
        <div className="mt-2">
          <select className={inputCls} value={form.professor_id}
            onChange={(e) => pickInstructor(e.target.value)} data-testid="off-instructor">
            <option value="">— instructor —</option>
            {instructors.map((i) => <option key={i._id} value={i._id}>{i.name}</option>)}
          </select>
          {voicePreview && (
            <div className="text-[11px] mt-1 text-slate-500" data-testid="off-voice-preview">
              Voice profile at creation (LOCKED at creation): <b>{voicePreview.voice_model_ref}</b> · v{voicePreview.version}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input className={inputCls} placeholder="Cohort" value={form.cohort_name}
            onChange={(e) => setForm({ ...form, cohort_name: e.target.value })} />
          <input className={inputCls} placeholder="Schedule" value={form.schedule}
            onChange={(e) => setForm({ ...form, schedule: e.target.value })} />
          <input className={inputCls} type="date" value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <input className={inputCls} type="date" value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <input className={inputCls + " mt-2"} placeholder="Office hours" value={form.office_hours}
          onChange={(e) => setForm({ ...form, office_hours: e.target.value })} />
        <button onClick={create} className={btnPrimary + " mt-3"} data-testid="off-submit"
          disabled={!form.master_course_id || !form.professor_id}>Create</button>
      </Card>

      <Card title={`Offerings (${offerings.length})`} testId="off-list">
        <ul className="space-y-1.5 text-sm">
          {offerings.map((o) => (
            <li key={o._id} className="border border-slate-100 rounded-lg px-3 py-2 flex items-center justify-between">
              <div>
                <div><b>{o.cohort_name || "(no cohort)"}</b> · MC v{o.master_course_version} · {badge(o.status)}</div>
                <div className="text-xs text-slate-500">Prof: {o.professor_id.slice(0, 8)} · Voice locked: {o.voice_profile_id?.slice(0, 8)}</div>
              </div>
              <ReassignButton offering={o} instructors={instructors} onDone={load} />
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function ReassignButton({ offering, instructors, onDone }) {
  const [showing, setShowing] = useState(false);
  const [pid, setPid] = useState("");
  return (
    <div>
      {!showing ? (
        <button className={btnGhost} onClick={() => setShowing(true)} data-testid={`off-reassign-${offering._id}`}>Reassign instructor</button>
      ) : (
        <div className="flex gap-1.5 items-center">
          <select className={inputCls + " w-auto"} value={pid} onChange={(e) => setPid(e.target.value)}>
            <option value="">— new —</option>
            {instructors.filter(i => i._id !== offering.professor_id).map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
          </select>
          <button className={btnPrimary} disabled={!pid} onClick={async () => {
            try {
              await axios.post(`${API}/kuhas/offerings/${offering._id}/reassign-instructor`, { new_professor_id: pid }, { headers: auth() });
              toast.success("Reassigned — voice atomically updated");
              setShowing(false); onDone();
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }}>Confirm</button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ 5. Revision Requests ═══════════════ */
function RevisionsTab() {
  const [approvedCourses, setApprovedCourses] = useState([]);
  const [approvedPrograms, setApprovedPrograms] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ target_type: "master_course", target_id: "", reason: "", proposed_change_ref: "" });

  const load = useCallback(async () => {
    const [c, p, r] = await Promise.all([
      axios.get(`${API}/kuhas/master-courses?status=approved`, { headers: auth() }),
      axios.get(`${API}/kuhas/programs?status=approved`, { headers: auth() }),
      axios.get(`${API}/kuhas/revision-requests`, { headers: auth() }),
    ]);
    setApprovedCourses(c.data.items || []);
    setApprovedPrograms(p.data.items || []);
    setReqs(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function submit() {
    try {
      let ref = form.proposed_change_ref;
      try { ref = JSON.parse(ref); } catch { /* keep as string */ }
      await axios.post(`${API}/kuhas/revision-requests`, {
        target_type: form.target_type, target_id: form.target_id, reason: form.reason,
        proposed_change_ref: ref,
      }, { headers: auth() });
      toast.success("Revision request submitted");
      setForm({ target_type: "master_course", target_id: "", reason: "", proposed_change_ref: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
  }

  const options = form.target_type === "master_course" ? approvedCourses : approvedPrograms;
  return (
    <>
      <Card title="Open a Revision Request" testId="rev-create">
        <select className={inputCls + " mb-2"} value={form.target_type}
          onChange={(e) => setForm({ ...form, target_type: e.target.value, target_id: "" })} data-testid="rev-target-type">
          <option value="master_course">Master Course</option>
          <option value="program">Program</option>
        </select>
        <select className={inputCls + " mb-2"} value={form.target_id}
          onChange={(e) => setForm({ ...form, target_id: e.target.value })} data-testid="rev-target-id">
          <option value="">— approved target —</option>
          {options.map((o) => (
            <option key={o._id} value={o._id}>
              {o.course_code || o.program_code} — {o.title} v{o.version}
            </option>
          ))}
        </select>
        <textarea className={inputCls + " mb-2"} rows={2} placeholder="Reason" value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="rev-reason" />
        <textarea className={inputCls + " mb-2"} rows={3} placeholder='Proposed change (text or JSON, e.g. {"delta":"..."})'
          value={form.proposed_change_ref} onChange={(e) => setForm({ ...form, proposed_change_ref: e.target.value })}
          data-testid="rev-change" />
        <button className={btnPrimary} onClick={submit} disabled={!form.target_id || !form.reason} data-testid="rev-submit">
          Submit revision request
        </button>
      </Card>
      <Card title={`All Revision Requests (${reqs.length})`} testId="rev-list">
        <ul className="text-sm space-y-1.5">
          {reqs.map((r) => (
            <li key={r._id} className="border border-slate-100 rounded-lg px-3 py-2">
              <div>{badge(r.review_status)} · {r.target_type} · target v{r.current_version}
                {r.resulting_version && <span className="ml-2">→ new v{r.resulting_version}</span>}
              </div>
              <div className="text-xs text-slate-500 italic mt-0.5">"{r.reason}"</div>
              <div className="text-[11px] text-slate-500">by {r.initiated_by} · {(r.created_at || "").slice(0, 16)}</div>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
